/*
 * Shared order-status helper for the tracking page and the order strip.
 *
 * HOW IT WORKS
 * ------------
 * Before checkout the storefront stamps a random tracking id onto the cart
 * as the attribute "zetse_track". That attribute travels with the order, so
 * Shopify Flow can file a metaobject under it as the order moves:
 *
 *   Order created   -> status "received",         order_number, placed_at
 *   Order fulfilled -> status "out_for_delivery"
 *   Delivered       -> status "delivered",        delivered_at
 *
 * This helper reads that record back through the Section Rendering API
 * (/search?q=<tracking id>&section_id=order-delivery-status). It is the only
 * identifier the customer's browser and the admin share - the thank-you page
 * runs on a shopify.com domain and cannot pass the order number back.
 *
 * Only a delivered record is cached; anything earlier is re-read on every
 * poll, because its status is still moving.
 */
window.ZetseDelivery = (function () {
  var ENABLED = true;

  var CACHE = 'zetse_delivered';
  var pending = {};

  function key(k) {
    return String(k == null ? '' : k).replace(/^#/, '').trim().toLowerCase();
  }

  function readCache() {
    try {
      var raw = localStorage.getItem(CACHE);
      var o = raw ? JSON.parse(raw) : null;
      return o && typeof o === 'object' ? o : {};
    } catch (e) { return {}; }
  }

  function writeCache(c) {
    try { localStorage.setItem(CACHE, JSON.stringify(c)); } catch (e) {}
  }

  /* Only ever holds finished orders - see lookup() */
  function cached(k) {
    if (!ENABLED) { return null; }
    var id = key(k);
    if (!id) return null;
    return readCache()[id] || null;
  }

  function isDone(rec) {
    return !!(rec && rec.status === 'delivered');
  }

  function lookup(k) {
    if (!ENABLED) { return Promise.resolve(null); }

    var id = key(k);
    if (!id) { return Promise.resolve(null); }

    var hit = cached(id);
    if (hit) { return Promise.resolve(hit); }
    if (pending[id]) { return pending[id]; }
    if (typeof fetch !== 'function') { return Promise.resolve(null); }

    var url = '/search?q=' + encodeURIComponent(id) + '&section_id=order-delivery-status';

    pending[id] = fetch(url, { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.text() : ''; })
      .then(function (html) {
        var m = html.match(/data-delivery-status[^>]*>([\s\S]*?)<\/script>/);
        if (!m) { return null; }

        var data = null;
        try { data = JSON.parse(m[1]); } catch (e) { return null; }
        if (!data || !data.found) { return null; }

        var rec = {
          order: data.order || '',
          placed: data.placed || '',
          delivered: data.delivered || '',
          status: data.status || 'received',
          minutes: 0
        };

        if (rec.placed && rec.delivered) {
          var diff = Date.parse(rec.delivered) - Date.parse(rec.placed);
          if (!isNaN(diff)) { rec.minutes = Math.max(0, Math.round(diff / 60000)); }
        }

        /* a finished order will never change again, so it can be kept */
        if (isDone(rec)) {
          var c = readCache();
          c[id] = rec;
          writeCache(c);
        }

        return rec;
      })
      .catch(function () { return null; })
      .then(function (v) { delete pending[id]; return v; });

    return pending[id];
  }

  /*
   * running   - still inside the promised window
   * delayed   - past the window, but only just
   * escalated - past the window by more than escalateMins
   * ontime    - delivered inside the promised window
   * late      - delivered, but over the promised window
   */
  function state(order, promiseMins, escalateMins, rec) {
    var mins = parseInt(order.mins, 10) || promiseMins;

    if (isDone(rec)) {
      return (rec.minutes && rec.minutes > mins) ? 'late' : 'ontime';
    }

    var end = order.start + mins * 60000;
    var now = Date.now();
    if (now > end + (escalateMins * 60000)) { return 'escalated'; }
    if (now > end) { return 'delayed'; }
    return 'running';
  }

  function forget(k) {
    var id = key(k);
    var c = readCache();
    if (c[id]) { delete c[id]; writeCache(c); }
  }

  return { enabled: ENABLED, key: key, cached: cached, lookup: lookup, state: state, isDone: isDone, forget: forget };
})();